import { useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAssessmentStore } from '../store/useAssessmentStore';
import {
  CREATE_COHORT,
  UPDATE_COHORT,
  Cohort,
  CreateCohortWithStaffMutation,
  CreateCohortWithStaffMutationVariables,
  UpdateCohortMutation,
  UpdateCohortMutationVariables,
} from '@edwin-group/cj-graphql';
import { useMutation } from '@apollo/client';
import { useMissionStore } from '../../shared/store/useMissionStore';
import { GetCurrentSchoolYearUseCase } from '../../use-cases/get-current-school-year';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { useRoot } from './useRoot';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { useAuthentication } from '@/hooks/useAuthentication';

export const classCountOptions = Array.from({ length: 10 }, (_, i) => ({
  label: `${i + 1}`,
}));

const cohortFormSchema = z.object({
  cohortDocumentId: z.string().optional(),
  missionAssessment: z.string(),
  yearGroupDocumentId: z.string().min(1, 'Please select a year group'),
  keyStageDocumentId: z.string(),
  schoolYear: z.string().min(1, 'Please select a school year'),
  howManyClasses: z.number().min(1, 'Please select number of classes'),
  classNames: z
    .array(
      z.object({
        name: z.string().min(1, 'Class name is required'),
      }),
    )
    .min(1, 'At least one class name is required'),
  cohortName: z.string().min(1, 'Cohort name is required'),
});

type CohortFormData = z.infer<typeof cohortFormSchema>;

export interface CohortFormProps {
  mission?: {
    assessment?: string;
    keyStage?: string;
  };
  onSubmit?: (data: CohortFormData) => void;
  loading?: boolean;
  cohortsEmpty?: boolean;
}

export const useCohortForm = (cohortsEmpty?: boolean) => {
  const { yearGroupsResult, keyStagesResult } = useRoot();
  const { schoolContext } = useAuthentication();
  const assessmentPage = useAssessmentStore((state) => state.assessmentPage);
  const cohortToUpdate = useAssessmentStore((state) => state.cohortToUpdate);
  const setCohortToUpdate = useAssessmentStore((state) => state.setCohortToUpdate);
  const mission = useMissionStore((state) => state.mission);
  const navigate = useNavigate();
  const [createCohort, { loading: loadingCreationSubmit }] = useMutation<
    CreateCohortWithStaffMutation,
    CreateCohortWithStaffMutationVariables
  >(CREATE_COHORT, {
    refetchQueries: 'all',
  });

  const [updateCohort, { loading: loadingUpdateSubmit }] = useMutation<
    UpdateCohortMutation,
    UpdateCohortMutationVariables
  >(UPDATE_COHORT, {
    refetchQueries: 'all',
  });

  const { toast } = useToast();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CohortFormData>({
    resolver: zodResolver(cohortFormSchema),
    defaultValues: {
      missionAssessment: mission?.roleModel,
      keyStageDocumentId: mission?.keyStage?.documentId,
      schoolYear: GetCurrentSchoolYearUseCase.execute(),
      howManyClasses: 1,
      classNames: [{ name: '' }],
      cohortName: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'classNames',
  });

  const watchedClassCount = watch('howManyClasses');
  const watchedKeyStageDocumentId = watch('keyStageDocumentId');
  const keyStage = useMemo(() => {
    if (!watchedKeyStageDocumentId) return undefined;
    return keyStagesResult?.keyStages?.find((keyStage) => keyStage?.documentId === watchedKeyStageDocumentId);
  }, [keyStagesResult?.keyStages, watchedKeyStageDocumentId]);

  useEffect(() => {
    const currentFieldsLength = fields.length;
    const targetLength = watchedClassCount;

    if (targetLength > currentFieldsLength) {
      for (let i = currentFieldsLength; i < targetLength; i++) {
        append({ name: '' });
      }
    } else if (targetLength < currentFieldsLength) {
      for (let i = currentFieldsLength - 1; i >= targetLength; i--) {
        remove(i);
      }
    }
  }, [watchedClassCount, fields.length, append, remove]);

  useEffect(() => {
    if (!mission) return;

    if (!cohortToUpdate) {
      setValue('missionAssessment', mission?.roleModel);
      setValue('keyStageDocumentId', mission?.keyStage?.documentId || '');
      setValue('schoolYear', GetCurrentSchoolYearUseCase.execute());
      setValue('howManyClasses', 1);
      setValue('classNames', [{ name: '' }]);
      setValue('cohortName', '');
      setValue('yearGroupDocumentId', '');
      setValue('cohortDocumentId', undefined);
      return;
    }

    setValue('cohortDocumentId', cohortToUpdate.documentId);
    setValue('missionAssessment', mission?.roleModel);
    setValue('keyStageDocumentId', mission?.keyStage?.documentId || '');
    setValue('cohortName', cohortToUpdate.name);
    setValue('yearGroupDocumentId', cohortToUpdate.yearGroup?.documentId || '');
    setValue('schoolYear', cohortToUpdate.schoolYear || '');
    setValue('howManyClasses', cohortToUpdate?.classes?.length || 1);
    setValue('classNames', cohortToUpdate.classes?.map((className) => ({ name: className?.name || '' })) || []);
  }, [cohortToUpdate, mission, setValue]);

  const handleFormSubmit = (data: CohortFormData) => {
    const isUpdate = !!cohortToUpdate;
    const formData = {
      name: data.cohortName,
      school: schoolContext?.documentId,
      schoolYear: data.schoolYear,
      yearGroup: data.yearGroupDocumentId,
      keyStage: data.keyStageDocumentId,
      classes: data.classNames.map((className) => ({
        name: className.name,
      })),
    };

    const promise = isUpdate
      ? updateCohort({
          variables: {
            documentId: cohortToUpdate.documentId,
            data: formData,
          },
        })
      : createCohort({
          variables: {
            data: formData,
          },
        });

    promise
      .then((res) => {
        const documentId =
          (
            (res.data as CreateCohortWithStaffMutation).createCohortWithStaff ||
            (res.data as UpdateCohortMutation).updateCohort
          )?.documentId || '';

        toast({
          title: 'Success',
          description: `Cohort ${cohortToUpdate ? 'updated' : 'created'} successfully`,
        });
        if (isUpdate) {
          trackGAEvent('cohort_updated', {
            cohort_name: formData.name,
            cohort_document_id: documentId,
            mission_name: mission?.roleModel ?? '',
            mission_document_id: mission?.documentId ?? '',
          });
          setCohortToUpdate(null);
        } else {
          trackGAEvent('cohort_created', {
            cohort_name: formData.name,
            cohort_document_id: documentId,
            mission_name: mission?.roleModel ?? '',
            mission_document_id: mission?.documentId ?? '',
          });
          setCohortToUpdate(null);
        }

        if (cohortsEmpty) {
          setCohortToUpdate({ ...formData, documentId } as unknown as Cohort);
          return;
        }

        navigate(-1);
      })
      .catch((err) => {
        toast({
          title: 'Error',
          description: `Error ${cohortToUpdate ? 'updating' : 'creating'} cohort` + err?.message,
          variant: 'destructive',
        });
      });
  };

  return {
    mission,
    assessmentPage,
    control,
    formState: { errors },
    year: GetCurrentSchoolYearUseCase.execute(),
    fields,
    yearGroupsResult,
    keyStage,
    loading: loadingCreationSubmit || loadingUpdateSubmit,
    handleSubmit,
    handleFormSubmit,
  };
};
